import React from 'react';
import { CheckIcon, GraduationCapIcon, MonitorIcon, ShieldCheckIcon, TrophyIcon } from 'lucide-react';
import { MatchedSchool } from '../services/matchingApi';
import { normalizeSchool } from '../utils/schoolFields';
import { extractListItems } from '../utils/sanitizeHtml';

/** Partners send 1–8 bullets; cards must stay comparable in height. */
const MAX_BULLETS = 3;
interface MatchedSchoolCardProps {
  school: MatchedSchool;
  index: number;
}

/**
 * One API-sourced school in the results list.
 *
 * Mobile and desktop are laid out separately because they answer the question
 * differently. On desktop there is room for a rank column, the partner headline,
 * and a dedicated action column. On mobile the logo IS the identity — it is
 * given roughly a third of the card beside the selling points, the headline is
 * dropped, and the action spans the full width. The hidden layout is
 * display:none, so it is out of the tab order and the accessibility tree.
 */
export function MatchedSchoolCard({
  school,
  index
}: MatchedSchoolCardProps) {
  const normalized = normalizeSchool(school as Record<string, unknown>);
  const {
    name,
    logo,
    accreditation,
    headline,
    clickUrl,
    highlight
  } = normalized;
  const bullets = extractListItems(normalized.bodyHtml).slice(0, MAX_BULLETS);
  const position = ((school as Record<string, unknown>).mPosition || index + 1) as number;
  const isFirst = index === 0;
  const initials = (name || 'SC').split(' ').filter(Boolean).map(w => w[0]).join('').slice(0, 3).toUpperCase();
  const rankCircle = <div className={`w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center flex-shrink-0 ${isFirst ? 'bg-navy text-white' : 'border-2 border-gray-200 text-gray-500'}`} data-id="element-651" data-mp="1saenfo4eq22z">
      <span className="text-sm font-bold" data-id="element-652" data-mp="10ji8mhysdnd2">{position}</span>
      <span className="sr-only" data-id="element-653" data-mp="10gw1ukjpzbvw">Ranked number {position}</span>
    </div>;
  const highlightPill = highlight ? <span className="inline-flex items-center text-center bg-light border border-gray-200 text-navy text-[11px] sm:text-xs font-semibold leading-tight px-2.5 py-1 rounded-full" data-id="element-654" data-mp="27vho7cddirhn">
      {highlight}
    </span> : null;
  const logoBlock =
  // h-full matters: without it the image's max-h-full has no height to
  // resolve against and a tall wordmark overflows onto the school name.
  <div className="w-full h-full flex items-center justify-center" data-id="element-655" data-mp="2cokar0jqh0oc">
      {logo ? <img src={logo} alt={`${name} logo`} className="max-w-full max-h-full object-contain" loading="lazy" data-id="element-656" data-mp="1oypzgnf2bk67" /> : <div className="flex flex-col items-center gap-1 text-navy" data-id="element-657" data-mp="1xy38jez9y6qe">
          <GraduationCapIcon className="w-7 h-7" aria-hidden="true" data-id="element-658" data-mp="1tv6xc9c2dcwh" />
          <span className="text-sm font-bold" data-id="element-659" data-mp="1ty2sxr0fvphr">{initials}</span>
        </div>}
    </div>;
  const bulletList = bullets.length > 0 ? <ul className="space-y-2" data-id="element-660" data-mp="359tz43el891u">
        {bullets.map(bullet => <li key={bullet} className="flex items-start gap-2" data-id="element-661" data-mp="3cuo4lqp23yqv">
            <CheckIcon className="w-4 h-4 mt-0.5 flex-shrink-0 text-green-600" aria-hidden="true" data-id="element-662" data-mp="3cwh9p1k0uawr" />
            <span className="text-[13px] sm:text-sm text-gray-700 leading-relaxed" data-id="element-663" data-mp="32wqcut7znjti">
              {bullet}
            </span>
          </li>)}
      </ul> : null;
  const accreditationLine = accreditation ? <p className="inline-flex items-center gap-1.5 text-[11px] sm:text-xs text-gray-500" data-id="element-664" data-mp="3vlxbds7g09er">
      <ShieldCheckIcon className="w-3.5 h-3.5 flex-shrink-0 text-green-600" aria-hidden="true" data-id="element-665" data-mp="4eumj7f2uf7p" />
      {accreditation}
    </p> : null;
  const ctaButton = clickUrl ? <a href={clickUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center text-center w-full min-h-[56px] lg:min-h-[60px] px-5 py-4 text-base font-semibold rounded-full text-white bg-primary shadow-ds-md hover:bg-primary-hover hover:shadow-ds-lg lg:hover:-translate-y-0.5 active:bg-secondary focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary transition-[background-color,box-shadow,transform] duration-150 ease-out" data-id="element-666" data-mp="3qtnsux8nqbmi">
      View Programs
    </a> : null;
  const onlineNote = <p className="flex items-center justify-center gap-1.5 text-xs font-semibold text-gray-600" data-id="element-667" data-mp="2gpnvl9dcr32r">
      <MonitorIcon className="w-3.5 h-3.5 flex-shrink-0 text-green-600" aria-hidden="true" data-id="element-668" data-mp="3ecwfizxpg42n" />
      100% Online
    </p>;
  return <article className="relative" data-id="element-669" data-mp="39fw9gp0uv1bj">
      {isFirst && <div className="rounded-t-xl px-4 py-2.5 sm:py-3 flex items-center justify-center gap-2 bg-navy" data-id="element-670" data-mp="34likb4csdqfb">
          <TrophyIcon className="w-4 h-4 flex-shrink-0 text-gold-light" aria-hidden="true" data-id="element-671" data-mp="2zn7b8vubfjy5" />
          <span className="text-white text-xs sm:text-sm font-bold tracking-wide" data-id="element-672" data-mp="zt8f2490aw6">
            #1 Recommended School
          </span>
        </div>}

      <div className={`bg-white transition-shadow duration-200 ease-out ${isFirst ? 'rounded-b-xl border-2 border-t-0 border-navy/20 shadow-lg hover:shadow-xl' : 'rounded-xl border border-gray-200 shadow-sm hover:shadow-md'}`} data-id="element-673" data-mp="4gpfkgx7utt7">
        {/* ---------- Mobile ---------- */}
        <div className="lg:hidden p-4" data-id="element-674" data-mp="igqehqtx7r61">
          {/* Rank reads as "#1" rather than a bare circle — at this size a
              number on its own is ambiguous. */}
          <div className="flex items-center justify-between gap-3 pb-3" data-id="element-675" data-mp="rf269w6iwv02">
            <span className={`inline-block flex-shrink-0 rounded-full border px-2.5 py-1 text-xs font-bold leading-none ${isFirst ? 'bg-[#14479E] border-[#14479E] text-white' : 'bg-[#F4F7FC] border-gray-200 text-[#14479E]'}`} data-id="element-676" data-mp="w6bssnwrzi7q">
              {`#${position}`}
              <span className="sr-only" data-id="element-677" data-mp="1b4cj92evx1uh"> ranked school</span>
            </span>
            {highlightPill}
          </div>

          {/* The logo carries identity on mobile, so it leads at full width —
              which also lets the bullets run the width of the card. */}
          <div className="h-20 flex items-center justify-center overflow-hidden" data-id="element-678" data-mp="v6jzkta0h125">
            {logoBlock}
          </div>

          <div className="mt-4 text-center" data-id="element-679" data-mp="qdqu236pxop0">
            <h3 className="text-lg font-bold text-navy leading-tight" data-id="element-680" data-mp="145x89jqb9llt">
              {name}
            </h3>
            {accreditationLine && <div className="mt-1 flex justify-center" data-id="element-681" data-mp="z73vjtiulymp">
                {accreditationLine}
              </div>}
          </div>

          {bulletList && <div className="mt-3 pt-3 border-t border-gray-100" data-id="element-682" data-mp="196l0g8qa08hm">
              {bulletList}
            </div>}

          <div className="mt-4 space-y-2" data-id="element-683" data-mp="3fwj0wjwjwbvs">
            {ctaButton}
            {onlineNote}
          </div>
        </div>

        {/* ---------- Desktop ---------- */}
        <div className="hidden lg:flex p-7 gap-8" data-id="element-684" data-mp="1qelwbul2pza7">
          <div className="flex-1 min-w-0 flex gap-4" data-id="element-685" data-mp="1qgykiphfd5vp">
            {rankCircle}

            <div className="flex-1 min-w-0" data-id="element-686" data-mp="1vd5jfybelh45">
              <h3 className="text-2xl font-bold text-navy leading-tight" data-id="element-687" data-mp="1gpc6kldpcsp9">
                {name}
              </h3>
              {accreditationLine && <div className="mt-2" data-id="element-688" data-mp="1lmaxyw5zeljr">{accreditationLine}</div>}
              {headline && <p className="mt-4 text-lg font-bold text-crimson leading-snug" data-id="element-689" data-mp="17qgz75yx9ckd">
                  {headline}
                </p>}
              {bulletList && <div className="mt-4" data-id="element-690" data-mp="xr860fbvs1z2">{bulletList}</div>}
            </div>
          </div>

          <div className="w-60 flex-shrink-0 border-l border-gray-100 pl-8 flex flex-col items-center justify-center gap-4" data-id="element-691" data-mp="12ohu9rnja7lb">
            {highlightPill}
            <div className="w-full h-20 flex items-center justify-center" data-id="element-692" data-mp="2s2ek17ki9257">
              {logoBlock}
            </div>
            {ctaButton}
            {onlineNote}
          </div>
        </div>
      </div>
    </article>;
}
export function DidYouKnowBanner() {
  return <p className="flex flex-wrap items-center justify-center gap-x-2 py-3 text-sm text-gray-600" data-id="element-693" data-mp="1fve0god8ck9q">
      <span className="font-bold text-gray-800" data-id="element-694" data-mp="1b2kxa4xetjzr">DID YOU KNOW?</span>
      <span data-id="element-695" data-mp="165ufnd5yigc8">Online enrollments continue to grow year over year.</span>
    </p>;
}